import {
    gql,
    NetworkStatus,
    type LazyQueryExecFunction,
    useLazyQuery,
    useQuery,
} from "@apollo/client"
import { useCallback, useMemo, useState } from "react"

import {
    OUTFITTER_ASSISTANT_AGENT,
    OUTFITTER_ASSISTANT_CONVERSATION_HISTORY_PAGE_SIZE,
    OUTFITTER_ASSISTANT_CONVERSATION_MESSAGE_PAGE_SIZE,
} from "~components/outfitter-assistant/outfitter-assistant-constants"
import {
    mergeConversationPages,
    toUiMessages,
    type AssistantConversationMessagesQueryResult,
    type AssistantConversationsQueryResult,
} from "~utils/helpers/outfitter-assistant-conversation-history-helpers"

const LOAD_CONVERSATION_MESSAGES_ERROR =
    "Unable to load conversation messages." as const

export const OUTFITTER_ASSISTANT_CONVERSATIONS_QUERY = gql`
    query OutfitterAssistantConversations(
        $input: AiConversationsInput
        $paging: Paging!
    ) {
        aiConversations(input: $input, paging: $paging) {
            data {
                id
                title
                last_message_at
                last_message_preview
                message_count
                created_at
            }
            count
        }
    }
`

export const OUTFITTER_ASSISTANT_CONVERSATION_MESSAGES_QUERY = gql`
    query OutfitterAssistantConversationMessages(
        $conversationId: ID!
        $paging: Paging!
    ) {
        aiConversationMessages(
            conversationId: $conversationId
            paging: $paging
        ) {
            data {
                id
                role
                text
                created_at
                parts {
                    type
                    ... on AiConversationTextPart {
                        text
                    }
                    ... on AiConversationFilePart {
                        media_type
                        url
                        filename
                    }
                    ... on AiConversationUnknownPart {
                        data
                    }
                }
            }
            count
            pages
        }
    }
`

export default function useOutfitterAssistantConversationHistory() {
    const [page, setPage] = useState(0)
    const variables = useMemo(
        () => ({
            input: {
                ai_agent: OUTFITTER_ASSISTANT_AGENT,
            },
            paging: {
                page: 0,
                size: OUTFITTER_ASSISTANT_CONVERSATION_HISTORY_PAGE_SIZE,
            },
        }),
        []
    )
    const { data, loading, error, fetchMore, networkStatus } =
        useQuery<AssistantConversationsQueryResult>(
            OUTFITTER_ASSISTANT_CONVERSATIONS_QUERY,
            {
                variables,
                fetchPolicy: "cache-and-network",
                notifyOnNetworkStatusChange: true,
            }
        )
    const [
        loadMessagesQuery,
        { loading: isLoadingMessages, error: messagesError },
    ] = useLazyQuery<
        AssistantConversationMessagesQueryResult,
        ConversationMessagesQueryVariables
    >(OUTFITTER_ASSISTANT_CONVERSATION_MESSAGES_QUERY, {
        fetchPolicy: "network-only",
    })
    const loadConversationMessages =
        useLoadConversationMessages(loadMessagesQuery)
    const conversations = data?.aiConversations.data ?? []
    const totalCount = data?.aiConversations.count ?? 0
    const hasMore = conversations.length < totalCount
    const isLoadingMore = networkStatus === NetworkStatus.fetchMore
    const loadMore = useCallback(async () => {
        if (!hasMore || isLoadingMore) return

        const nextPage = page + 1
        await fetchMore({
            variables: {
                ...variables,
                paging: {
                    page: nextPage,
                    size: OUTFITTER_ASSISTANT_CONVERSATION_HISTORY_PAGE_SIZE,
                },
            },
            updateQuery: mergeConversationPages,
        })
        setPage(nextPage)
    }, [fetchMore, hasMore, isLoadingMore, page, variables])

    return {
        conversations,
        isLoading: loading && conversations.length === 0,
        error: error ?? messagesError,
        hasMore,
        isLoadingMore,
        isLoadingMessages,
        loadMore,
        loadConversationMessages,
    }
}

function useLoadConversationMessages(
    loadMessagesQuery: LazyQueryExecFunction<
        AssistantConversationMessagesQueryResult,
        ConversationMessagesQueryVariables
    >
) {
    return useCallback(
        async (conversationId: string) => {
            const result = await loadMessagesQuery({
                variables: {
                    conversationId,
                    paging: {
                        page: 0,
                        size: OUTFITTER_ASSISTANT_CONVERSATION_MESSAGE_PAGE_SIZE,
                    },
                },
            })
            const firstPage = getConversationMessagesPage(result)
            const nextPages = await Promise.all(
                getRemainingPageNumbers(firstPage.pages).map((messagePage) =>
                    loadMessagesQuery({
                        variables: {
                            conversationId,
                            paging: {
                                page: messagePage,
                                size: OUTFITTER_ASSISTANT_CONVERSATION_MESSAGE_PAGE_SIZE,
                            },
                        },
                    })
                )
            )

            return toUiMessages([
                ...firstPage.data,
                ...nextPages.flatMap(
                    (nextResult) => getConversationMessagesPage(nextResult).data
                ),
            ])
        },
        [loadMessagesQuery]
    )
}

function getConversationMessagesPage(result: {
    readonly data?: AssistantConversationMessagesQueryResult
    readonly error?: Error
}) {
    if (result.error) throw result.error
    const page = result.data?.aiConversationMessages
    if (!page) throw new Error(LOAD_CONVERSATION_MESSAGES_ERROR)

    return page
}

function getRemainingPageNumbers(totalPages: number): number[] {
    return Array.from({ length: Math.max(totalPages - 1, 0) }, (_, index) => {
        return index + 1
    })
}

type ConversationMessagesQueryVariables = {
    readonly conversationId: string
    readonly paging: {
        readonly page: number
        readonly size: number
    }
}
